import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import PriorityHighRoundedIcon from "@mui/icons-material/PriorityHighRounded";
import type { DateTime } from "luxon";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { OutreachApproval, type RequestOutreach } from "../../../utils/models";
import { OutreachApprovalDecidedMessage } from "./OutreachApprovalDecidedMessage";
import { OutreachApprovalFooterText } from "./OutreachApprovalFooterText";
import { OutreachApprovalShell, type OutreachApprovalTone } from "./OutreachApprovalShell";
import { OutreachApprovalWaitingActions } from "./OutreachApprovalWaitingActions";
import { OutreachApprovalWaitingMessage } from "./OutreachApprovalWaitingMessage";
import { OutreachAutoApproveFooter } from "./OutreachAutoApproveFooter";
import { ApprovalViewKind, approvalViewOf } from "./outreachApprovalView";

type Props = {
	outreach: RequestOutreach;
	now: DateTime;
	onReview: () => void;
};

/** Waiting pickups open the scheduling review; completed decisions show their outcome. */
export const OutreachApprovalCallout: FC<Props> = ({ outreach, now, onReview }) => {
	const { t, i18n } = useTranslation();
	const view = approvalViewOf(outreach, now, i18n.language);
	if (view === null) return null;
	const { requestId } = outreach;

	if (view.kind === ApprovalViewKind.Waiting) {
		const { signUp, recommended, wait, autoApprove, full } = view;
		const tone: OutreachApprovalTone = recommended && !full ? "success" : "warning";
		// Full: auto-approve wouldn't have helped. Off: what it would save. On: this one didn't fit its rules, so it needs you.
		const footer = full ? null : !autoApprove ? (
			<OutreachAutoApproveFooter
				text={`${t("request.outreach.approval.fill_faster")} ${t("request.outreach.approval.waiting_for", { name: signUp.name, wait })}`}
				requestId={requestId}
			/>
		) : (
			<OutreachApprovalFooterText>{t("request.outreach.approval.not_covered")}</OutreachApprovalFooterText>
		);
		return (
			<OutreachApprovalShell
				tone={tone}
				icon={tone === "success" ? <CheckRoundedIcon /> : <PriorityHighRoundedIcon />}
				message={<OutreachApprovalWaitingMessage view={view} tone={tone} />}
				actions={<OutreachApprovalWaitingActions onReview={onReview} />}
				footer={footer}
			/>
		);
	}

	const { signUp, savedWait } = view;
	const approved = signUp.approval !== OutreachApproval.NotApproved;
	return (
		<OutreachApprovalShell
			tone={approved ? "success" : "warning"}
			icon={approved ? <CheckRoundedIcon /> : <PriorityHighRoundedIcon />}
			message={<OutreachApprovalDecidedMessage view={view} />}
			actions={null}
			footer={
				savedWait === null ? null : (
					<OutreachAutoApproveFooter text={t("request.outreach.approval.done.saved", { name: signUp.name, wait: savedWait })} requestId={requestId} />
				)
			}
		/>
	);
};
